<template>
  <div>
    <v-card class="ma-4" elevation="2">
      <v-card-title>
        <v-icon left color="orange">mdi-content-copy</v-icon>
        Duplicate Review
        <v-chip x-small dark color="orange" class="ml-2">
          {{ pendingCount }} pending
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn-toggle
          v-model="scopeFilter"
          mandatory
          dense
          class="mr-2"
          @change="resetAndFetchQueue"
        >
          <v-btn small value="current">Current</v-btn>
          <v-btn small value="legacy">Legacy</v-btn>
        </v-btn-toggle>
        <v-btn-toggle
          v-model="statusFilter"
          mandatory
          dense
          @change="resetAndFetchQueue"
        >
          <v-btn small value="pending">Pending</v-btn>
          <v-btn small value="all">All</v-btn>
        </v-btn-toggle>
        <v-btn icon class="ml-2" :loading="loading" @click="fetchQueue">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle class="text-left">
        <strong>Current</strong> shows accounts detected by the review
        lifecycle; <strong>Legacy</strong> keeps the historical backlog
        available without mixing it into daily support work. New Talent accounts
        are flagged as possible duplicates of existing accounts. A
        <strong>hard</strong> hold blocks the shift feed, booking and Job Board
        until reviewed; a <strong>soft</strong> flag (every matched account in
        good standing) blocks nothing. <strong>Confirm</strong> keeps the hold
        permanent; <strong>Clear</strong> releases this pair immediately.
      </v-card-subtitle>
      <v-data-table
        :headers="headers"
        :items="rows"
        :loading="loading"
        :page="page"
        :items-per-page="limit"
        :server-items-length="total"
        :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
        disable-sort
        @update:page="setPage"
        @update:items-per-page="setLimit"
        dense
        class="elevation-0"
      >
        <template #[`item.flagged`]="{ item }">
          <router-link
            v-if="item.hygienistId"
            :to="`/talent?hygienistId=${item.hygienistId}`"
            style="color: #0B4598; text-decoration: none;"
          >
            #{{ item.hygienistId }} {{ item.flaggedFirstName }}
            {{ item.flaggedLastName }}
          </router-link>
          <span v-else class="text--secondary">unknown</span>
          <div class="text--secondary" style="font-size: 11px;">
            {{ item.flaggedEmail }}
          </div>
        </template>
        <template #[`item.matched`]="{ item }">
          <template v-if="item.matchedFirstName || item.matchedEmail">
            <router-link
              :to="`/talent?hygienistId=${item.duplicateHygienistId}`"
              style="color: #0B4598; text-decoration: none;"
            >
              #{{ item.duplicateHygienistId }} {{ item.matchedFirstName }}
              {{ item.matchedLastName }}
            </router-link>
            <div class="text--secondary" style="font-size: 11px;">
              {{ item.matchedEmail }}
            </div>
          </template>
          <span v-else class="text--secondary">
            #{{ item.duplicateHygienistId }} (deleted account)
          </span>
        </template>
        <template #[`item.matchScore`]="{ item }">
          <v-chip
            v-if="item.matchScore != null"
            x-small
            dark
            :color="item.matchScore >= 90 ? 'red' : 'orange'"
          >
            {{ item.matchScore }}
          </v-chip>
          <span v-else class="text--secondary">—</span>
        </template>
        <template #[`item.signals`]="{ item }">
          <span>
            <v-chip
              v-for="signal in signalChips(item)"
              :key="signal"
              x-small
              outlined
              color="deep-orange"
              class="mr-1"
            >
              {{ signal }}
            </v-chip>
            <span
              v-if="signalChips(item).length === 0"
              class="text--secondary"
              style="font-size: 11px;"
            >
              name only
            </span>
          </span>
        </template>
        <template #[`item.standing`]="{ item }">
          <v-chip
            x-small
            dark
            :color="item.matchedInGoodStanding ? 'green' : 'red'"
          >
            {{
              item.matchedInGoodStanding
                ? "good standing"
                : "restricted / flagged"
            }}
          </v-chip>
        </template>
        <template #[`item.holdTier`]="{ item }">
          <v-chip
            x-small
            dark
            :color="
              item.holdTier === 'hard'
                ? 'red'
                : item.holdTier === 'soft'
                ? 'blue-grey'
                : 'green'
            "
          >
            {{ item.holdTier || "none" }}
          </v-chip>
        </template>
        <template #[`item.createdAt`]="{ item }">
          <span style="font-size: 12px;">{{ formatDate(item.createdAt) }}</span>
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip
            x-small
            dark
            :color="
              (item.status || 'pending') === 'pending'
                ? 'orange'
                : item.status === 'confirmed'
                ? 'red'
                : 'green'
            "
          >
            {{ item.status || "pending" }}
          </v-chip>
        </template>
        <template #[`item.actions`]="{ item }">
          <span>
            <v-btn
              v-if="canConfirm(item)"
              x-small
              color="error"
              class="mr-1"
              :loading="resolvingId === item.id"
              :disabled="resolvingId !== null"
              @click="resolvePair(item, 'confirm')"
            >
              Confirm
            </v-btn>
            <v-btn
              v-if="canClear(item)"
              x-small
              color="success"
              outlined
              :loading="resolvingId === item.id"
              :disabled="resolvingId !== null"
              @click="resolvePair(item, 'clear')"
            >
              Clear
            </v-btn>
          </span>
          <span
            v-if="!canConfirm(item) && !canClear(item)"
            class="text--secondary"
            style="font-size: 11px;"
          >
            {{ item.resolvedAt ? formatDate(item.resolvedAt) : "resolved" }}
          </span>
        </template>
      </v-data-table>
    </v-card>
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000">
      {{ snackbar.text }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { getDuplicateReviewQueue, resolveDuplicate } from "@/api";
import {
  applyDuplicateResolution,
  applyDuplicateResolutionCounts,
  canClearDuplicate,
  canConfirmDuplicate,
  emptyDuplicateReviewCounts,
  normalizeDuplicateReviewQueueResponse
} from "@/utils/duplicateReview";

export default {
  name: "DuplicateReview",
  data() {
    return {
      rows: [],
      loading: false,
      scopeFilter: "current",
      statusFilter: "pending",
      page: 1,
      limit: 25,
      total: 0,
      counts: emptyDuplicateReviewCounts(),
      queueRequestId: 0,
      resolvingId: null,
      snackbar: { show: false, text: "", color: "success" },
      headers: [
        { text: "Flagged Talent (new)", value: "flagged", sortable: false },
        {
          text: "Matched Account (existing)",
          value: "matched",
          sortable: false
        },
        { text: "Score", value: "matchScore", sortable: true },
        { text: "Signals", value: "signals", sortable: false },
        { text: "Matched Standing", value: "standing", sortable: false },
        { text: "Hold", value: "holdTier", sortable: true },
        { text: "Detected", value: "createdAt", sortable: true },
        { text: "Status", value: "status", sortable: true },
        { text: "Actions", value: "actions", sortable: false }
      ]
    };
  },
  computed: {
    pendingCount() {
      return this.counts.pending;
    }
  },
  created() {
    this.fetchQueue();
  },
  methods: {
    async fetchQueue() {
      const requestId = ++this.queueRequestId;
      const requestStatus = this.statusFilter;
      const requestScope = this.scopeFilter;
      const requestPage = this.page;
      const requestLimit = this.limit;
      this.loading = true;
      try {
        const response = await getDuplicateReviewQueue(
          requestStatus,
          requestScope,
          requestPage,
          requestLimit
        );
        if (requestId !== this.queueRequestId) return;
        const state = normalizeDuplicateReviewQueueResponse(
          response,
          requestPage,
          requestLimit
        );
        this.rows = state.items;
        this.page = state.page;
        this.limit = state.limit;
        this.total = state.total;
        this.counts = state.counts;
      } catch (error) {
        if (requestId === this.queueRequestId) {
          this.showSnackbar("Failed to load duplicate review queue", "error");
        }
      } finally {
        if (requestId === this.queueRequestId) {
          this.loading = false;
        }
      }
    },
    resetAndFetchQueue() {
      this.page = 1;
      this.fetchQueue();
    },
    setPage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchQueue();
    },
    setLimit(limit) {
      if (limit === this.limit) return;
      this.limit = limit;
      this.page = 1;
      this.fetchQueue();
    },
    canConfirm(item) {
      return canConfirmDuplicate(item);
    },
    canClear(item) {
      return canClearDuplicate(item);
    },
    signalChips(item) {
      let signals = item && item.signals;
      if (typeof signals === "string") {
        try {
          signals = JSON.parse(signals);
        } catch (error) {
          signals = null;
        }
      }
      if (!signals) return [];
      const chips = [];
      if (signals.phoneMatch) chips.push("phone");
      if (signals.cdhoMatch) chips.push("CDHO");
      if (signals.postalCodeMatch) chips.push("postal");
      return chips;
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString() : "—";
    },
    showSnackbar(text, color) {
      this.snackbar = { show: true, text, color };
    },
    async resolvePair(item, action) {
      if (this.loading) {
        // Ignore any pre-decision queue snapshot that is still in flight; it
        // must not overwrite the locally applied committed result later.
        this.queueRequestId += 1;
        this.loading = false;
      }
      const requestView = {
        scope: this.scopeFilter,
        status: this.statusFilter,
        page: this.page,
        limit: this.limit,
        queueRequestId: this.queueRequestId
      };
      this.resolvingId = item.id;
      try {
        const previousStatus = item.status || "pending";
        const result = await resolveDuplicate(item.id, action);
        const tier = result && result.holdState ? result.holdState.tier : null;
        const suffix =
          action === "clear"
            ? tier === "hard"
              ? " — other pending matches still hold this account"
              : " — hold released, talent can view and book again"
            : " — hold stays in place";
        this.showSnackbar(
          `Pair ${action === "confirm" ? "confirmed" : "cleared"}${suffix}`,
          action === "confirm" ? "orange" : "success"
        );
        const viewChanged =
          requestView.scope !== this.scopeFilter ||
          requestView.status !== this.statusFilter ||
          requestView.page !== this.page ||
          requestView.limit !== this.limit ||
          requestView.queueRequestId !== this.queueRequestId;
        if (viewChanged) {
          // A filter/page/refresh request overlapped the decision and may
          // contain a pre-commit snapshot. Refetch the active view instead of
          // applying the old view's row/count delta to it.
          await this.fetchQueue();
          return;
        }
        this.rows = applyDuplicateResolution(
          this.rows,
          result,
          action,
          this.statusFilter
        );
        this.counts = applyDuplicateResolutionCounts(
          this.counts,
          previousStatus,
          result.status
        );
        this.total =
          this.statusFilter === "all"
            ? this.counts.total
            : this.counts[this.statusFilter];
      } catch (error) {
        this.showSnackbar("Failed to resolve duplicate pair", "error");
      } finally {
        this.resolvingId = null;
      }
    }
  }
};
</script>

<style scoped></style>
